import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import { copyStepGroup } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

// Copy a group into other workflows, either with its steps (each step's checklist/documents/
// fields/SLA copied, landing inside the new group even if the target already had the step
// elsewhere) or group-only (an empty group is created, and any step the target already has that
// matches one of the source group's step definitions is moved into it — nothing is added).
export default function CopyStepGroupModal({ open, onClose, group, categories, sourceCategoryId }) {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);
  const [withSteps, setWithSteps] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setWithSteps(true);
      setError('');
    }
  }, [open]);

  const targets = (categories || []).filter((c) => c.id !== sourceCategoryId);
  const allSelected = targets.length > 0 && selectedIds.length === targets.length;

  const toggle = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(allSelected ? [] : targets.map((c) => c.id));

  const mutation = useMutation({
    mutationFn: () => copyStepGroup(group.id, selectedIds, withSteps),
    onSuccess: (res) => {
      pushToast({ type: 'success', message: res?.message || 'Group copied.' });
      queryClient.invalidateQueries({ queryKey: ['workflow-categories'] });
      onClose();
    },
    onError: (err) =>
      setError(
        err.response?.data?.message ||
          Object.values(err.response?.data?.errors || {})[0]?.[0] ||
          'Could not copy the group.'
      ),
  });

  const handleCopy = () => {
    setError('');
    if (selectedIds.length === 0) {
      setError('Pick at least one workflow.');
      return;
    }
    mutation.mutate();
  };

  if (!group) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Copy "${group.name}" to other workflows`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCopy} loading={mutation.isPending} disabled={selectedIds.length === 0}>
            Copy to {selectedIds.length || ''} workflow{selectedIds.length === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label
            className={`flex cursor-pointer items-start gap-2 rounded-lg border-2 p-3 ${
              withSteps ? 'border-primary bg-primary-50' : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              className="mt-0.5 accent-primary"
              checked={withSteps}
              onChange={() => setWithSteps(true)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">Copy group with its steps</span>
              <span className="block text-xs text-gray-500">
                Creates the group and copies each step's checklist, required documents and fields into it. A step
                already active in the target keeps its position but is moved into the new group and has its
                configuration overwritten.
              </span>
            </span>
          </label>

          <label
            className={`flex cursor-pointer items-start gap-2 rounded-lg border-2 p-3 ${
              !withSteps ? 'border-primary bg-primary-50' : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              className="mt-0.5 accent-primary"
              checked={!withSteps}
              onChange={() => setWithSteps(false)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">Copy group only</span>
              <span className="block text-xs text-gray-500">
                Creates an empty group. Any step the target workflow already has that matches one of this group's
                steps is moved into it; steps the target doesn't have are not added.
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500">Workflows</p>
          <button type="button" onClick={toggleAll} className="text-xs text-primary hover:underline">
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {targets.map((category) => {
            const active = selectedIds.includes(category.id);
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => toggle(category.id)}
                aria-pressed={active}
                className={`rounded-lg border-2 p-3 text-left transition-colors ${
                  active ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{category.code}</p>
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded-full border text-[10px] ${
                      active ? 'border-primary bg-primary text-white' : 'border-gray-300 text-transparent'
                    }`}
                  >
                    ✓
                  </span>
                </div>
                <div className="mt-1">
                  <CategoryBadge category={category} />
                </div>
                <p className="mt-2 text-xs text-gray-500">{category.steps.length} steps</p>
              </button>
            );
          })}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
